import OlCluster from 'ol/source/Cluster';
import { ILayerWrapperOptions, LayerWrapper } from '@openlayers/LayerWrapper';
import * as olStyle from 'ol/style';
import { FeatureMouseInteraction } from '@openlayers/interaction/FeatureMouseInteraction';
import Feature from 'ol/Feature';
import { isCluster, latLonToEN } from '@openlayers/ol-utils';
import { Point as OlPoint } from 'ol/geom';
import { PageStateStore } from '@shared/user-state/PageStateStore';
import { reaction } from 'mobx';

export class PageStateLayer extends LayerWrapper<IPageState> {
    featureType: FeatureType = 'page-state';
    clusterSource: any = null;

    singleStyle = new olStyle.Style({
        image: new olStyle.Icon({
            scale: 0.8,
            src:
                `data:image/svg+xml;utf8,` +
                escape(`<svg xmlns="http://www.w3.org/2000/svg" width="35" height="35" viewBox="0 0 73 101" xml:space="preserve">
<g transform="translate(-63.1 -24.6)">
    <path style="fill:#3f4244;fill-opacity:1;stroke-width:.102517" d="M96.5 82.1h5.9v43.5h-5.9z"/>
    <circle style="fill:#f0640a;fill-opacity:1;stroke-width:.121212" cx="99.7" cy="61.2" r="36.6"/>
    <path style="fill:#fff;stroke-width:.0978444" d="M85 41h27.2v40.4L98.7 71.3 85 81.3Z"/>
  </g>
</svg>`),
            anchor: [0.5, 1],
        }),
    });

    constructor(opts: ILayerWrapperOptions<IPageState>, private store: PageStateStore) {
        super({
            ...opts,
            makeFeature,
            styleFunction: feature => {
                if (isCluster(feature)) {
                    return [this.renderGroupStyle(feature.get('features').length)];
                }
                return [this.singleStyle];
            },
        });

        this.clusterSource = new OlCluster({
            distance: 20,
            source: this.layerSource,
        });

        this.layer.setSource(this.clusterSource);
        this.layer.set('id', this.featureType);

        reaction(() => this.store.itemsToShowOnMap, this.redrawDebounced);
        this.redraw();
    }

    redraw = () => {
        this.allFeatures = [];
        this.layerSource.clear(true);
        this.drawAll(this.store.itemsToShowOnMap);
        this.layerSource.dispatchEvent('change');
    };

    redrawDebounced = _.debounce(this.redraw, 100);

    groupStyleCache = {};

    renderGroupStyle(count: number = 0) {
        const cacheKey = count;
        if (this.groupStyleCache[cacheKey]) return this.groupStyleCache[cacheKey];

        const style = new olStyle.Style({
            image: new olStyle.Icon({
                scale: 0.8,
                src: `data:image/svg+xml;utf8,` + makeGroupIcon(count),
                anchor: [0.5, 1],
            }),
        });

        this.groupStyleCache[cacheKey] = style;
        return style;
    }
}

function makeFeature(state: IPageState, mouseInteraction?: FeatureMouseInteraction): Feature {
    if (!state) {
        return null;
    }

    if (!state.lat) {
        return null;
    }

    const coord: Coordinate = latLonToEN(state.lat, state.lon);
    if (!coord) {
        return null;
    }

    const feature = new Feature({
        geometry: new OlPoint(coord),
    });

    feature.setId(state.uuid);
    feature.setStyle(this.style);
    feature.set('type', 'page-state');
    feature.set('data', state);
    feature.set('no_hover_style', true);
    feature.set('mouseInteraction', mouseInteraction);

    return feature;
}

function makeGroupIcon(count = 1) {
    return escape(`<svg xmlns="http://www.w3.org/2000/svg" width="35" height="35" viewBox="0 0 95 103" xml:space="preserve"><circle style="fill:white;stroke:#8f8f8f;stroke-width:30;stroke-miterlimit:10;" cx="374.3" cy="376" r="364.7"/>
<g transform="translate(-52.8 -164)">
    <path style="fill:#3f4244;fill-opacity:1;stroke-width:.102517" d="M98.7 221.5h5.9V265h-5.9z"/>
    <path style="fill:#3f4244;fill-opacity:1;stroke-width:.102517" transform="rotate(9.5)" d="M146.9 200.7h5.9v43.5h-5.9z"/>
    <ellipse style="fill:#f89d62;fill-opacity:1;stroke-width:.115067" cx="145.9" cy="183.6" transform="rotate(9.5) skewX(-.1)" rx="34.9" ry="34.7"/>
    <path style="fill:#3f4244;fill-opacity:1;stroke-width:.102517" transform="rotate(-10.9)" d="M40.9 235.6h5.9v43.5h-5.9z"/>
    <ellipse style="fill:#f89d62;fill-opacity:1;stroke-width:.116793" cx="47.3" cy="218.3" transform="matrix(.98255 -.186 .192 .9814 0 0)" rx="35.6" ry="35"/>
    <circle style="fill:#f0640a;fill-opacity:1;stroke-width:.121212" cx="101.9" cy="200.5" r="36.6"/>
    <path style="fill:#fff;stroke-width:.0978444" d="M87.2 180.3h27.2v40.4l-13.5-10.1-13.7 10.1Z"/>
    <text style="font-weight:700;font-size:40px;font-family:Arial;stroke:black;fill:white;stroke-width:1.5;stroke-miterlimit:10" x="109" y="255">${count}</text>
  </g></svg>`);
}
